import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PageGen } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * BingoBase - Base class for Bingo game cards
 *
 * Each bingo card is a 3×3 grid that randomly selects 9 items from all available items.
 * Subclasses define different grid arrangements (e.g., 2×3, 3×4, 4×4, 4×5).
 *
 * Migrated from C# EduFrame.Img10.Generators.Bingo
 */
export abstract class BingoBase implements ILayoutLabelGen, IItemCount {
  protected abstract gridCountX: number;
  protected abstract gridCountY: number;

  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const {random} = opts.data;
    const margin = PdfUnit.FromMillimeters(6);
    const cellSize = new PdfSize(
      (PdfSize.A4.width / this.gridCountX - 2 * margin) / 3,
      (PdfSize.A4.height / this.gridCountY - 2 * margin) / 3
    );

    const pages: PageGen[] = [];
    for (let p = 0; p < 3; p++) {
      pages.push(pdf.addPage());
    }

    for (let pageIdx = 0; pageIdx < 2; pageIdx++) {
      const page = pages[pageIdx];
      const cardSize = new PdfSize(
        PdfSize.A4.width / this.gridCountX,
        PdfSize.A4.height / this.gridCountY
      );
      const innerW = cellSize.width;
      const innerH = cellSize.height;

      for (let j = 1; j < this.gridCountX; j++) {
        page.cutCross(new PdfPoint(j * cardSize.width, 0));
      }
      for (let j = 1; j < this.gridCountY; j++) {
        page.cutCross(new PdfPoint(0, j * cardSize.height));
      }

      for (let cx = 0; cx < this.gridCountX; cx++) {
        for (let cy = 0; cy < this.gridCountY; cy++) {
          const indices = Array.from({ length: opts.list.length }, (_, i) => i);
          for (let k = indices.length - 1; k > 0; k--) {
            const j = random.next(0, k + 1);
            [indices[k], indices[j]] = [indices[j], indices[k]];
          }
          const selected = indices.slice(0, 9);

          const cardTexts = selected.map((idx) => opts.list[idx].left.getText() ?? '');
          const cardFont = page.adjustFontMulti(
            cardTexts.map((t) => [t]),
            GUtil.baseFont,
            cellSize
          );

          for (let ix = 0; ix < 3; ix++) {
            for (let iy = 0; iy < 3; iy++) {
              const idx = ix * 3 + iy;
              if (idx >= selected.length) continue;

              const rect = new PdfRect(
                margin + cx * cardSize.width + ix * innerW,
                margin + cy * cardSize.height + iy * innerH,
                innerW,
                innerH
              );

              page.drawLabel(opts.list[selected[idx]].left, { rect, font: cardFont });
              page.drawRectangle(GUtil.basePen, null, rect);
            }
          }
        }
      }
    }

    const page3 = pages[2];
    const page3Margin = PdfUnit.FromMillimeters(10);
    const page3CellSize = Math.min(cellSize.width, cellSize.height);
    const page3InnerW =
      (PdfSize.A4.width - 2 * page3Margin) /
      Math.floor((PdfSize.A4.width - 2 * page3Margin) / page3CellSize);
    const page3InnerH = page3CellSize;
    const countX = Math.max(1, Math.floor((PdfSize.A4.width - 2 * page3Margin) / page3CellSize));
    const countY = Math.ceil(opts.list.length / countX);

    const rightTexts = opts.list.map((item) => item.right.getText() ?? '');
    const answerFont = page3.adjustFontMulti(
      rightTexts.map((t) => [t]),
      GUtil.baseFont,
      new PdfSize(page3InnerW, page3InnerH)
    );

    for (let i = 0; i < opts.list.length; i++) {
      const col = i % countX;
      const row = Math.floor(i / countX);
      const rect = new PdfRect(
        page3Margin + col * page3InnerW,
        page3Margin + row * page3InnerH,
        page3InnerW,
        page3InnerH
      );
      page3.drawLabel(opts.list[i].right, { rect, font: answerFont });
      page3.drawRectangle(GUtil.cutPen, null, rect);
    }
  }
}

export class Bingo__2x3 extends BingoBase {
  protected gridCountX = 2;
  protected gridCountY = 3;
}

export class Bingo__3x4 extends BingoBase {
  protected gridCountX = 3;
  protected gridCountY = 4;
}

export class Bingo__4x4 extends BingoBase {
  protected gridCountX = 4;
  protected gridCountY = 4;
}

export class Bingo__4x5 extends BingoBase {
  protected gridCountX = 4;
  protected gridCountY = 5;
}
